import { Action } from "redux";
import { api } from "../../shared/api/system/apiClient";
import * as ApiParams from "../../shared/api/system/apiParameters";
import { neverHere } from "@travellocal/utils/tsHelpers";
import SharedActionPromises from "../../shared/state/actionPromises";
import { StoreActionFactory } from "../../shared/state/storeActionFactory";
import { SharedActions } from "./../../shared/flux/actions/sharedActions";
import { EntityConverter } from "./../../shared/helpers/entityConverter";
import { LocalStorageHelper } from "@travellocal/utils/localStorageHelper";
import { Actions } from "./actions";

export class ActionPromises extends SharedActionPromises {
  public static allLtos() {
    return StoreActionFactory.api(Actions.Ltos.GetAll, api.getLtos({ pageSize: 500 }));
  }

  public static allCurrencies() {
    return StoreActionFactory.api(Actions.General.GetAllCurrencies, api.getAllCurrencies());
  }

  public static getReview(reviewId: number) {
    return StoreActionFactory.api(Actions.Reviews.GetReview, api.getReview(reviewId));
  }

  public static getReviewsPage(ltoId?: number, page?: number) {
    const pageNumber = page < 1 ? 1 : page;

    return ltoId
      ? StoreActionFactory.api(Actions.Reviews.GetReviewPage, api.getLtoReviews(ltoId, pageNumber), { page, ltoId })
      : StoreActionFactory.api(Actions.Reviews.GetReviewPage, api.getAllReviews(pageNumber), { page });
  }

  public static createReview(review: IReviewInput) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Reviews.CreateReview, api.createReview(review)))
        .then(r => d(ActionPromises.getReviewsPage(review.ltoId, -1)));
    });
  }

  public static updateReview(review: IReviewUpdateInput) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Reviews.UpdateReview, api.updateReview(review)))
        .then(r => d(ActionPromises.getReview(review.id)));
    });
  }

  public static publishReview(reviewId: number) {
    return StoreActionFactory.api(Actions.Reviews.PublishReview, api.publishReview(reviewId));
  }

  public static deleteReview(reviewId: number) {
    return StoreActionFactory.api(Actions.Reviews.DeleteReview, api.deleteReview(reviewId));
  }

  public static replyToReview(reviewReply: IReviewReplyInput) {
    return StoreActionFactory.api(Actions.Reviews.ReplyToReview, api.replyToReview(reviewReply));
  }

  public static profile() {
    return StoreActionFactory.api(Actions.General.GetProfile, api.getProfile());
  }

  public static updateExchangeRates(input: IExchangeRatesEdit) {
    const newInput = EntityConverter.currencyToExchangeRatesUpdateInput(input);
    return StoreActionFactory.api(Actions.General.UpdateExchangeRates, api.updateExchangeRate(newInput));
  }

  // Locations
  public static allRegions() {
    return StoreActionFactory.api(Actions.Locations.GetAllRegions, api.getLocations({ locationType: LocationType.Region }));
  }

  public static allCountries() {
    return StoreActionFactory.api(Actions.Locations.GetAllCountries, api.getLocations({ locationType: LocationType.Country, pageSize: 100 }));
  }

  public static getLocationBySlug(locationSlug: string) {
    return StoreActionFactory.api(Actions.Locations.GetLocationBySlug, api.getLocationBySlug(locationSlug));
  }

  /**
   * Get config for all LTOs in a location
   */
  public static getLtoConfigForLocation(locationId: number) {
    return StoreActionFactory.api(Actions.LtoLocation.GetConfig, api.getLtoConfigForLocation(locationId));
  }

  /**
   * Get config for all locations attached to an LTO
   */
  public static getLocationConfigForLto(locationId: number) {
    return StoreActionFactory.api(Actions.LtoLocation.GetConfig, api.getLocationConfigForLto(locationId));
  }

  // Themes
  public static getThemePage(filters: ApiParams.ThemesFilterSet, page: number, pageSize: number = 10) {
    const pageNumber = page < 1 ? 1 : page;
    return StoreActionFactory.api(Actions.Themes.GetThemePage, api.getThemePage(pageNumber, pageSize, filters), { page, pageSize, params: filters });
  }

  // Users
  public static updateUserProfile(input: IUserSummary) {
    const user: IUserUpdate = EntityConverter.userSummaryToUserUpdate(input);
    return StoreActionFactory.api(Actions.Users.UpdateUserProfile, api.updateUserProfile(user), { userId: user.id });
  }

  public static getUserProfile(userId: number) {
    return StoreActionFactory.api(Actions.Users.GetUserProfile, api.getUserProfile(userId), { userId });
  }

  // Enquiries
  public static allEnquiriesForCustomer() {
    return StoreActionFactory.api(Actions.Enquiries.GetEnquiriesForCustomer, api.getEnquiriesForCustomer());
  }

  public static toggleEnquiryAdminFollow(enquiryId: number, follow: boolean) {
    return StoreActionFactory.thunk(async dispatch => {
      await api.toggleEnquiryAdminFollow(enquiryId, follow);
      return dispatch(StoreActionFactory.api(Actions.Enquiries.ToggleAdminFollow, api.getEnquiry(enquiryId)));
    });
  }

  public static getContentPage(filters: IListContentParams, page: number, pageSize = 10) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params: filters };

    return StoreActionFactory.thunk<IAppStoreState>((d, getState) => {
      const state = getState();
      return d(StoreActionFactory.api(Actions.Content.GetContentPage, api.getContentPage(filters, pageNumber, pageSize), context));
    });
  }

  public static getContentForEdit(id: number) {
    return StoreActionFactory.thunk<IAppStoreState>((d, getState) => {
      const state = getState();
      return d(StoreActionFactory.api(Actions.Content.GetContentForEdit, api.getContentById(String(id))));
    });
  }

  public static saveContentFromEditor(content: Partial<IContent>) {
    const input = EntityConverter.contentToUpdateInput(content);
    return StoreActionFactory.api(Actions.Content.SaveContentFromEditor, api.updateContent(input));
  }

  public static deleteThemeFromEditor(id: number) {
    return StoreActionFactory.api(Actions.Themes.ClearThemeEditor, api.deleteTheme(id));
  }

  public static clearThemeEditor() {
    return StoreActionFactory.action(Actions.Themes.ClearThemeEditor, null);
  }

  public static getThemeForEdit(id: number) {
    return StoreActionFactory.api(Actions.Themes.GetThemeForEdit, api.getThemeById(id));
  }

  public static saveThemeFromEditor(theme: ITheme) {
    const shouldCreate = theme.id == null;
    const action = shouldCreate ? api.createTheme(theme) : api.updateTheme(theme);
    return StoreActionFactory.api(Actions.Themes.SaveThemeFromEditor, action);
  }

  // Enquiries

  public static allEnquiryStats() {
    return StoreActionFactory.api(Actions.Enquiries.Admin.Stats, api.getAllEnquiryStats());
  }

  public static ltoEnquiryStats(ltoId: number) {
    return StoreActionFactory.api(Actions.Enquiries.Lto.Stats, api.getLtoEnquiryStats(ltoId));
  }

  public static ltoFocusEnquiries(ltoId: number) {
    return StoreActionFactory.api(Actions.Enquiries.Lto.Focus, api.getLtoFocusEnquiries(ltoId));
  }

  public static ltoEnquiriesPage(actionName: string, page: number, pageSize: number, ltoId?: number, apiParams?: ApiParams.LtoEnquiryFilterSet) {
    const pageNumber = page < 1 ? 1 : page;

    const getLtoEnquiries = (id: number) => {
      const context = { id, page, pageSize, params: apiParams };
      return StoreActionFactory.api(actionName, api.getLtoEnquiriesPage(id, page, pageSize, apiParams), context);
    };

    if (!ltoId) {
      return StoreActionFactory.thunk((d, s) => {
        const state = s() as IAppStoreState;
        if (state.user.data) {
          ltoId = (state.user.data as ILtoEmployee).lto.id;
          return d(getLtoEnquiries(ltoId));
        }
      });
    }
    else {
      return getLtoEnquiries(ltoId);
    }
  }

  public static getEnquiriesPage(page: number, pageSize: number, apiParams?: ApiParams.AdminEnquiryFilterSet) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params: apiParams };
    return StoreActionFactory.api(Actions.Enquiries.GetEnquiriesPage, api.getEnquiriesPage(pageNumber, pageSize, apiParams), context);
  }

  public static getTriagedEnquiries() {
    return StoreActionFactory.api(Actions.Enquiries.GetTriagedEnquiries, api.getTriagedEnquiries());
  }

  /**
   * Fetch enquiry and related objects required for consistency (lto, currencies)
   */
  public static enquiry(enquiryId: number) {
    return StoreActionFactory.thunk<IAppStoreState>(async (dispatch, getState) => {
      const result = await dispatch(StoreActionFactory.api(Actions.Enquiries.GetEnquirySingle, api.getEnquiry(enquiryId), { id: enquiryId }));
      const enquiry = result.value.json.data;

      const { lto } = getState();
      const actions: Action[] = [];

      if (enquiry && enquiry.isConnected) {
        if (!lto.data || enquiry.lto.id !== lto.data.id) {
          // Optimisation - only refetch if different
          actions.push(ActionPromises.ltoById(enquiry.lto.id));
        }
      }
      else {
        actions.push(StoreActionFactory.action(Actions.Ltos.GetLtoById, null));
      }

      if (enquiry && enquiry.bookedTime != null) {
        // Fetch fixed exchange rates to use with price calcs
        actions.push(
          StoreActionFactory.api(
            Actions.EnquiryItineraries.GetCurrenciesForQuote,
            api.getCurrenciesForQuote(enquiry.currentQuoteId),
          ),
        );
      }
      else {
        // Clear fixed exchange rates if: 1. not fixed for enquiry 2. enquiry is null
        actions.push(
          StoreActionFactory.action(Actions.EnquiryItineraries.GetCurrenciesForQuote, null),
        );
      }

      await Promise.all(actions.map(a => dispatch(a)));
    });
  }

  public static updateEnquiry(enquiryId: number, update: IEnquiryUpdate) {
    return StoreActionFactory.api(Actions.Enquiries.UpdateEnquiry, api.updateEnquiry(enquiryId, update));
  }

  public static enquiryByReference(reference: string) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Enquiries.GetEnquirySingle, api.getEnquiryByReference(reference))).then(() => {
        const state = s() as IAppStoreState;
        if (state.enquiry.data.bookedTime) {
          return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetCurrenciesForQuote, api.getCurrenciesForQuote(state.enquiry.data.currentQuoteId)));
        }
        else {
          return d(StoreActionFactory.action(Actions.EnquiryItineraries.GetCurrenciesForQuote, null));
        }
      });
    });
  }

  // Ltos
  public static ltoById(ltoId: number) {
    return StoreActionFactory.api(Actions.Ltos.GetLtoById, api.getLtoByIdOrSlug(ltoId), { id: ltoId });
  }

  public static ltoTermsById(ltoId: number) {
    return StoreActionFactory.api(Actions.Ltos.GetLtoTermsById, api.getLtoTerms(ltoId), { id: ltoId });
  }

  public static ltoEmployeesByLtoId(ltoId?: number) {
    return StoreActionFactory.thunk<IAppStoreState>((d, s) => {
      const store = s();
      const getEmployees = () => StoreActionFactory.api(Actions.Ltos.GetAllEmployees, api.getEmployeesByLtoId(ltoId), { id: ltoId });

      return store.lto.data && store.lto.data.id === ltoId
        ? d(getEmployees())
        : d(ActionPromises.ltoById(ltoId)).then(() => d(getEmployees()));
    });
  }

  public static ltosAdminStats(params?: ApiParams.AdminLtoStatsFilterSet) {
    return StoreActionFactory.api(Actions.Ltos.GetAdminStats, api.getLtosAdminStats(params));
  }

  public static clearCurrentLto() {
    return StoreActionFactory.action(Actions.Ltos.ClearCurrentLto, null);
  }

  // Sample Itineraries
  public static sampleItinerariesByLtoId(ltoId: number) {
    const context = { id: ltoId, page: -1 }; // page of -1 resets the store, this api doesn't have paging so should always reset

    return StoreActionFactory.thunk((d, s) => {
      const state = s() as IAppStoreState;
      if (state.lto.data && state.lto.data.id === ltoId) {
        return d(StoreActionFactory.api(Actions.SampleItineraries.GetSampleItinerariesByLtoId, api.getSampleItinerariesForLto(ltoId, state.lto.data.currency.code), context));
      }

      return d(this.ltoById(ltoId))
        .then(() => d(this.sampleItinerariesByLtoId(ltoId)));
    });
  }

  public static sampleItineraryBySlug(itinerarySlug: string, ltoId: number) {
    return StoreActionFactory.thunk((d, s) => {
      const state = s() as IAppStoreState;
      if (state.lto.data) {
        return d(StoreActionFactory.api(Actions.SampleItineraries.GetSampleItineraryBySlug, api.getSampleItinerary(itinerarySlug, state.lto.data.currency.code)));
      }

      return d(this.ltoById(ltoId))
        .then(() => d(this.sampleItineraryBySlug(itinerarySlug, ltoId)));
    });
  }

  public static sampleItineraryBySlugByUsersLtoId(itinerarySlug: string) {
    return StoreActionFactory.thunk((d, s) => {
      const state = s() as IAppStoreState;
      if (state.user.data) {
        const ltoUser = state.user.data as ILtoEmployee;
        d(StoreActionFactory.api(Actions.SampleItineraries.GetSampleItineraryBySlug, api.getSampleItinerary(itinerarySlug, ltoUser.lto.currency.code)));
        return;
      }
    });
  }

  public static deleteSampleItinerary(itinerarySlug: string) {
    return StoreActionFactory.api(Actions.SampleItineraries.DeleteSampleItinerary, api.deleteSampleItinerary(itinerarySlug));
  }

  // Enquiry Itinerary
  public static paymentsForEnquiryId(enquiryId?: number, statuses?: PaymentStatus[]) {
    return StoreActionFactory.api(Actions.Enquiries.GetPaymentsForEnquiry, api.getPaymentsForEnquiry(enquiryId, statuses));
  }

  public static draftEnquiryItineraryById(enquiryId?: number) {
    return StoreActionFactory.thunk((d, s) => {
      const state = s() as IAppStoreState;
      if (state.enquiry.data) {
        if (state.enquiry.data.draftItineraryId) {
          d(StoreActionFactory.api(
            Actions.EnquiryItineraries.GetDraftItineraryById,
            api.getItinerary(state.enquiry.data.draftItineraryId),
            { id: state.enquiry.data.draftItineraryId },
          ));
        }
        return;
      }
      d(this.enquiry(enquiryId)).then(() => {
        d(this.draftEnquiryItineraryById(null));
      });
    });
  }

  /**
   * Fetch an enquiry, its current itinerary, and its associated quote.
   */
  public static enquiryItineraryAndQuoteFromEnquiryId(enquiryId: number) {
    return StoreActionFactory.thunk<IAppStoreState>(async (dispatch, getState) => {
      await dispatch(ActionPromises.enquiry(enquiryId));

      const state = getState();
      const quoteId = state.enquiry.data?.currentQuoteId;
      if (quoteId) {
        return dispatch(ActionPromises.enquiryItineraryAndQuoteFromQuoteId(quoteId));
      }
    });
  }

  /**
   * Fetch an enquiry itinerary and its associated quote.
   */
  public static enquiryItineraryAndQuoteFromQuoteId(quoteId: number) {
    return StoreActionFactory.thunk<IAppStoreState>((dispatch, getState) => {
      let state = getState();

      const enquiry = state.enquiry.data;
      const itinerary = state.liveEnquiryItinerary.data;
      const quote = state.liveEnquiryItineraryQuote.data;

      let shouldFetch = true;
      if (enquiry?.currentItinerarySummary && itinerary && quote) {
        // No need to get itinerary or quote again if it's already in the store. They don't go out of date.
        // NB quote.id is the id of the itinerary associated with the quote.
        const expectedItineraryId = enquiry.currentItinerarySummary.id;
        shouldFetch = enquiry.currentQuoteId !== quoteId ||
          itinerary.id !== expectedItineraryId ||
          quote.id !== expectedItineraryId;
      }

      if (!shouldFetch) {
        return;
      }

      return dispatch(
        StoreActionFactory.api(Actions.EnquiryItineraries.GetLiveItineraryQuoteById, api.getItineraryByQuote(quoteId)))
        .then(() => {
          state = getState();
          if (state.liveEnquiryItineraryQuote.data) {
            const itineraryId = state.liveEnquiryItineraryQuote.data.id;
            return dispatch(
              StoreActionFactory.api(Actions.EnquiryItineraries.GetLiveItineraryById, api.getItinerary(itineraryId)));
          }
        });
    });
  }

  public static confirmCancellation(enquiryId: number) {
    return StoreActionFactory.api(Actions.Enquiries.ConfirmEnquiryCancellation, api.cancel(enquiryId));
  }

  public static unrequestCancellation(enquiryId: number) {
    return StoreActionFactory.api(Actions.Enquiries.UnrequestEnquiryCancellation, api.unrequestCancellation(enquiryId));
  }

  public static updateSampleItineraryStatus(input: ISampleItineraryStatusUpdateInput) {
    return StoreActionFactory.api(Actions.SampleItineraries.UpdateSampleItineraryStatus, api.updateSampleItineraryStatus(input));
  }

  // Permissions
  public static getUserGlobalPermissions(userId?: number) {
    if (!userId) {
      return StoreActionFactory.thunk((d, s) => {
        const state = s() as IAppStoreState;
        if (state.user.data) {
          userId = state.user.data.id;
          d(StoreActionFactory.api(Actions.Permissions.GetUserGlobalPermissions, api.getUserGlobalPermissions(userId)));
          return;
        }
      });
    } else {
      StoreActionFactory.api(Actions.Permissions.GetUserGlobalPermissions, api.getUserGlobalPermissions(userId));
    }
  }

  // Authentication
  public static login(loginRequest: ILoginRequest) {
    const promise = api.login(loginRequest).then(loginResponse => {
      LocalStorageHelper.setItem("authtoken", loginResponse.json.token);

      return api.getProfile().then(profile => {
        const login: ILoginResponse = {
          ...loginResponse.json,
          user: profile.json.data,
        };

        return login;
      });
    });

    return StoreActionFactory.action(SharedActions.Authentication.Login, promise, { from: "Login request" });
  }

  public static loginWithToken(tokenResponse: IAuthorizeGrantResponse) {
    LocalStorageHelper.setItem("authtoken", tokenResponse.token);

    const promise = api.getProfile().then(profile => {
      const login: ILoginResponse = {
        ...tokenResponse,
        user: profile.json.data,
      };

      return login;
    });

    return StoreActionFactory.action(SharedActions.Authentication.Login, promise, { from: "With token" });
  }

  public static verify(input: IVerifyRequest) {
    return StoreActionFactory.action(SharedActions.Authentication.CompleteRegistration, api.verify(input));
  }

  public static forgotPassword(input: IForgottenPasswordRequest) {
    return StoreActionFactory.action(SharedActions.Authentication.ForgottenPassword, api.forgotPassword(input));
  }

  public static completeForgotPassword(input: IResetPasswordRequest) {
    return StoreActionFactory.action(SharedActions.Authentication.CompleteForgottenPassword, api.resetPassword(input));
  }

  public static logout() {
    return StoreActionFactory.action(SharedActions.Authentication.Logout, true);
  }

  // LTO
  public static createLto(input: INewLtoInput) {
    return StoreActionFactory.api(Actions.Ltos.CreateLto, api.createLto(input));
  }

  public static updateLtoSettings(lto: ILto) {
    const input = EntityConverter.ltoToLtoSettingsUpdateInput(lto);
    return StoreActionFactory.api(Actions.Ltos.UpdateLtoSettings, api.updateLtoSettings(input));
  }

  public static updateLtoProfile(input: ILtoProfileUpdateInput) {
    return StoreActionFactory.api(Actions.Ltos.UpdateLtoProfile, api.updateLtoProfile(input));
  }

  public static updateLtoEmployee(ltoEmployee: IEditableLtoEmployee) {
    const input = EntityConverter.editableLtoEmployeeToILtoEmployeeUpdateInput(ltoEmployee);
    return StoreActionFactory.api(Actions.Ltos.UpdateLtoEmployee, api.updateLtoEmployee(input));
  }

  public static addLtoEmployee(ltoEmployee: INewLtoEmployeeInput) {
    return StoreActionFactory.api(Actions.Ltos.AddLtoEmployee, api.addLtoEmployee(ltoEmployee));
  }

  // LTO-Location
  public static setLtoLocationConfig(locationId: number, ltoId: number, config: Partial<ILtoLocationConfig>) {
    return StoreActionFactory.api(Actions.LtoLocation.SetConfig, api.setLtoLocationConfig(locationId, ltoId, config));
  }

  // Location
  public static updateLocationStatus(status: ILocationStatusUpdateInput) {
    return StoreActionFactory.api(Actions.Locations.GetLocationBySlug, api.setLocationStatus(status));
  }

  public static linkLocationImage(input: ILinkLocationImageInput) {
    return StoreActionFactory.api(Actions.Locations.GetLocationBySlug, api.linkLocationImage(input));
  }

  public static unlinkLocationImage(input: ILinkLocationImageInput) {
    return StoreActionFactory.api(Actions.Locations.GetLocationBySlug, api.unlinkLocationImage(input));
  }

  public static deleteImage(imageId: number) {
    return StoreActionFactory.api(Actions.Images.DeleteImage, api.deleteImage(imageId));
  }

  public static deleteImageForLocation(imageId: number, locationSlug: string) {
    return StoreActionFactory.api(Actions.Locations.GetLocationBySlug, api.deleteLocationImage(imageId).then(() => api.getLocationBySlug(locationSlug)));
  }

  public static getCountriesByRegionSlug(regionSlug: string) {
    return StoreActionFactory.api(Actions.Locations.GetCountriesByRegionSlug, api.getCountriesByRegionSlug(regionSlug));
  }

  public static getDestinationsByCountrySlug(countrySlug: string) {
    return StoreActionFactory.api(Actions.Locations.GetDestinationsByCountrySlug, api.getDestinationsByCountrySlug(countrySlug));
  }

  public static updateLocationImageDescription(input: ILocationImage) {
    const newInput = EntityConverter.locationImagetoLocationImageUpdateInput(input);
    return StoreActionFactory.action(Actions.Locations.UpdateLocationImageDescription, api.updateLocationImageDescription(newInput));
  }

  public static createLocation(location: INewLocationInput) {
    return StoreActionFactory.api(Actions.Locations.CreateLocation, api.createLocation(location));
  }

  public static updateLocation(location: ILocationUpdateInput) {
    return StoreActionFactory.api(Actions.Locations.UpdateLocation, api.updateLocation(location));
  }

  public static updateLocationWeather(locationWeather: ILocationWeatherUpdateInput) {
    return StoreActionFactory.api(Actions.Locations.UpdateLocationWeather, api.updateLocationWeather(locationWeather));
  }

  // TODO get itinerary api needs the slug for sample itineraries; other places it needs the id
  public static getItinerary(itineraryId: number | string, itineraryType: ItineraryType) {
    if (itineraryType === ItineraryType.Sample && typeof (itineraryId) === "string") {
      return StoreActionFactory.api(Actions.SampleItineraries.GetSampleItineraryBySlug, api.getSampleItinerary(itineraryId));
    }
    else if (itineraryType === ItineraryType.Enquiry && typeof (itineraryId) === "number") {
      return StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(itineraryId));
    }
    else {
      throw new Error(`Could not get itinerary ${itineraryId} (type ${itineraryType}), type of id didn't match itinerary type`);
    }
  }

  public static getDayById(itineraryDayId: number) {
    return StoreActionFactory.api(Actions.Itineraries.GetItineraryDayById, api.getItineraryDay(itineraryDayId), { id: itineraryDayId });
  }

  public static addDay(getItineraryId: number | string, itineraryId: number, itineraryType: ItineraryType) {
    if (!getItineraryId) {
      return;
    }

    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Itineraries.CreateNewItineraryDay, api.createNewItineraryDay({ itineraryId })))
        .then(r1 => d(this.getItinerary(getItineraryId, itineraryType))
          .then(r2 => d(this.getDayById(r1.value.json.id))),
        );
    });
  }

  // TODO move/ delete/ get day endpoints use slug for sample itineraies
  public static deleteDay(itineraryId: number | string, itineraryType: ItineraryType, dayId: number) {
    if (!itineraryId) {
      return;
    }

    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Itineraries.DeleteItineraryDay, api.deleteItineraryDay(dayId)))
        .then(_ => d(this.getItinerary(itineraryId, itineraryType)));
    });
  }

  public static saveDayEvent(itineraryDayId: number, input: IItineraryDayEvent) {
    const newInput = EntityConverter.itineraryDayEventToItineraryDayEventUpdateInput(input);
    return StoreActionFactory.api(Actions.Itineraries.UpdateEventForItineraryDay, api.updateEventForItineraryDay(newInput));
  }

  public static addDayEvent(input: IAddEventToItineraryDayInput) {
    return StoreActionFactory.api(Actions.Itineraries.AddEventToItineraryDay, api.addEventToItineraryDay(input));
  }

  public static deleteDayEvent(eventId: number) {
    return StoreActionFactory.api(Actions.Itineraries.DeleteEventForItineraryDay, api.deleteEventForItineraryDay(eventId));
  }

  public static reorderEventsInItineraryDay(dayId: number, eventIds: number[]) {
    return StoreActionFactory.api(Actions.Itineraries.ReorderItineraryDayEvents, api.setEventOrderInItineraryDay(dayId, eventIds));
  }

  public static createEvent(event: Partial<IExcursion | ITransfer>) {
    switch (event.type) {
      case ComponentType.Excursion:
        const newExcursion = EntityConverter.excursionToNewExcursionInput(event);
        return StoreActionFactory.api(Actions.Itineraries.CreateNewExcursion, api.createNewExcursion(newExcursion));
      case ComponentType.Transfer:
        const newTransfer = EntityConverter.transferToNewTransferInput(event);
        return StoreActionFactory.api(Actions.Itineraries.CreateNewTransfer, api.createNewTransfer(newTransfer));
      default:
        neverHere(event, `Unknown eventType: ${(event as any).type}`);
    }
  }

  public static deleteEvent(event: Partial<IExcursion | ITransfer>, pageSize?: number) {
    return StoreActionFactory.api(Actions.Itineraries.DeleteEvent, api.deleteEvent(event.id));
  }

  public static updateEvent(event: Partial<IEvent>) {
    let input;
    switch (event.type) {
      case ComponentType.Excursion:
        input = EntityConverter.excursionToExcursionUpdateInput(event as IExcursion);
        return StoreActionFactory.api(Actions.Itineraries.UpdateExcursion, api.updateExcursion(input));
      case ComponentType.Transfer:
        input = EntityConverter.transferToTransferUpdateInput(event as ITransfer);
        return StoreActionFactory.api(Actions.Itineraries.UpdateTransfer, api.updateTransfer(input));
      default:
        neverHere(event, `Unknown eventType: ${(event as any).type}`);
        break;
    }
  }

  public static getExcursionsPage(ltoId: number, page: number, pageSize: number = 20, params?: ApiParams.ComponentApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    let apiPromise: Promise<IFetchApiResponse<IApiResponse<IExcursion[]>>>;

    if (!ltoId) {
      apiPromise = api.getAllExcursionsByQuery(pageNumber, pageSize, params);
    } else {
      apiPromise = api.getExcursionsByQuery(ltoId, pageNumber, pageSize, params);
    }

    return StoreActionFactory.api(Actions.Itineraries.GetExcursionsPage, apiPromise, context);
  }

  public static getTransfersPage(ltoId: number, page: number, pageSize: number = 20, params?: ApiParams.ComponentApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    let apiPromise: Promise<IFetchApiResponse<IApiResponse<ITransfer[]>>>;

    if (!ltoId) {
      apiPromise = api.getAllTransfersByQuery(pageNumber, pageSize, params);
    } else {
      apiPromise = api.getTransfersByQuery(ltoId, pageNumber, pageSize, params);
    }

    return StoreActionFactory.api(Actions.Itineraries.GetTransfersPage, apiPromise, context);
  }

  public static getAccommodation(id: number) {
    return StoreActionFactory.thunk(async dispatch => {
      return dispatch(StoreActionFactory.api(Actions.Itineraries.GetAccommodation, api.getAccommodationById(id)));
    });
  }

  public static getAccommodationPage(ltoId: number, page: number, pageSize: number = 20, params?: ApiParams.ComponentApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    let apiPromise: Promise<IFetchApiResponse<IApiResponse<IAccommodation[]>>>;

    if (!ltoId) {
      apiPromise = api.getAllAccommodationsByQuery(pageNumber, pageSize, params);
    } else {
      apiPromise = api.getAccommodationsByQuery(ltoId, pageNumber, pageSize, params);
    }

    return StoreActionFactory.api(Actions.Itineraries.GetAccommodationPage, apiPromise, context);
  }

  public static createAccommodationAndAddToDay(ltoId: number, itineraryDayId: number, accommodation: IAccommodation) {
    return StoreActionFactory.thunk((d, s) => {
      const createAccommodationPromise = d(StoreActionFactory.api(Actions.Itineraries.CreateNewAccommodation, api.createNewAccommodation(ltoId, accommodation)));
      return createAccommodationPromise.then((r) => {
        const p = api.setAccommodationForItineraryDay({
          accommodationId: r.value.json.id,
          includesBreakfast: false,
          includesLunch: false,
          includesDinner: false,
          itineraryDayId,
          notes: "",
          roomDescription: "",
        });

        return d(StoreActionFactory.api(Actions.Itineraries.SetAccommodationForItineraryDay, p));
      });
    });
  }

  public static createAccommodation(acc: Partial<IAccommodation>) {
    const ltoId = acc.ltoId;
    return StoreActionFactory.api(Actions.Itineraries.CreateNewAccommodation, api.createNewAccommodation(ltoId, acc),
    );
  }

  public static updateAccommodation(acc: Partial<IAccommodation>) {
    return StoreActionFactory.api(Actions.Itineraries.UpdateAccommodation, api.updateAccommodation(acc));
  }

  public static deleteAccommodation(ltoId: number, accommodation: IAccommodation) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Itineraries.DeleteAccommodation, api.deleteAccommodation(accommodation.id)))
        .then(r => d(ActionPromises.getAccommodationPage(ltoId, -1)));
    });
  }

  /**
   * Update the fields of day accommodation if day accommodation already exists, accommodation is already linked.
   * This endpoint will fail if the accommodation object is different; you must delete and recreate day accommodation.
   */
  public static saveDayAccommodation(itineraryDayId: number, input: IItineraryDayAccommodation) {
    const newInput = EntityConverter.itineraryDayAccommodationToItineraryDayAccommodationUpdate(input);
    return StoreActionFactory.api(Actions.Itineraries.UpdateAccommodationForItineraryDay, api.updateAccommodationForItineraryDay(itineraryDayId, newInput));
  }

  /**
   * Add a accommodation to a day if the day doesn't already have accommodation.
   */
  public static addDayAccommodation(input: ISetAccommodationForItineraryDayInput) {
    return StoreActionFactory.api(Actions.Itineraries.SetAccommodationForItineraryDay, api.setAccommodationForItineraryDay(input));
  }

  public static deleteDayAccommodation(dayId: number) {
    return StoreActionFactory.api(Actions.Itineraries.DeleteAccommodationForItineraryDay, api.deleteAccommodationForItineraryDay(dayId));
  }

  public static setAccommodationTypeForItineraryDay(dayId: number, input: IItineraryDayAccommodationTypeUpdateInput) {
    return StoreActionFactory.api(Actions.Itineraries.SetAccommodationTypeForItineraryDay, api.setAccommodationTypeForItineraryDay(dayId, input));
  }

  public static toggleEnquiryConfirmation(enquiryId: number, confirmed: boolean) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.Enquiries.ToggleEnquiryStatus, api.toggleEnquiryConfirmed(enquiryId, confirmed))).then(() => {
        return d(StoreActionFactory.api(Actions.Enquiries.GetEnquirySingle, api.getEnquiry(enquiryId), { id: enquiryId }));
      });
    });
  }

  // Sample Itinerary
  public static createSampleItinerary(input: INewSampleItineraryInput) {
    return StoreActionFactory.api(Actions.SampleItineraries.CreateSampleItinerary, api.createNewSampleItinerary(input));
  }

  public static saveSampleItinerary(input: ISampleItinerarySummary) {
    const newInput = EntityConverter.sampleItinerarySummaryToInputUpdate(input);
    return StoreActionFactory.api(Actions.SampleItineraries.UpdateSampleItinerary, api.updateSampleItinerary(newInput));
  }

  // Enquiry Itinerary
  public static saveEnquiryItinerary(input: IEnquiryItinerarySummary) {
    const newInput = EntityConverter.enquiryItinerarySummaryToInputUpdate(input);
    return StoreActionFactory.api(Actions.EnquiryItineraries.UpdateEnquiryItinerary, api.updateEnquiryItinerary(newInput));
  }

  public static createNewItinerary(input: INewEnquiryItineraryInput) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.EnquiryItineraries.CreateNew, api.createNewItinerary(input))).then(r => {
        return d(StoreActionFactory.api(Actions.Enquiries.GetEnquirySingle, api.getEnquiry(input.enquiryId), { id: input.enquiryId })).then(r2 => {
          return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(r.value.json.id), { id: r.value.json.id }));
        });
      });
    });
  }

  public static clearCurrentItinerary() {
    return StoreActionFactory.action(Actions.EnquiryItineraries.ClearCurrentItinerary, null);
  }

  public static updatePricingInfo(input: IEnquiryItinerarySummary) {
    const newInput = EntityConverter.enquiryItinerarySummaryToIEnquiryItineraryPricingInformationUpdateInput(input);
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.EnquiryItineraries.UpdatePricingInformation, api.updateEnquiryItineraryPricingInformation(newInput))).then(r => {
        return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(input.id)));
      });
    });
  }

  public static addLineItem(itineraryId: number, input: IEnquiryItineraryLineItem) {
    const newInput = EntityConverter.enquiryItineraryLineItemToNewEnquiryItineraryLineItemInput(itineraryId, input);
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.EnquiryItineraries.AddLineItem, api.createEnquiryItineraryLineItem(newInput))).then(r => {
        return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(itineraryId), { id: itineraryId }));
      });
    });
  }

  public static updateLineItem(itineraryId: number, input: IEnquiryItineraryLineItem) {
    const newInput = EntityConverter.enquiryItineraryLineItemToIEnquiryItineraryLineItemUpdateInput(itineraryId, input);
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.EnquiryItineraries.UpdateLineItem, api.updateEnquiryItineraryLineItem(newInput))).then(r => {
        return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(itineraryId), { id: itineraryId }));
      });
    });
  }

  public static removeLineItem(itineraryId: number, lineItemId: number) {
    return StoreActionFactory.thunk((d, s) => {
      return d(StoreActionFactory.api(Actions.EnquiryItineraries.RemoveLineItem, api.deleteEnquiryItineraryLineItem(lineItemId))).then(r => {
        return d(StoreActionFactory.api(Actions.EnquiryItineraries.GetDraftItineraryById, api.getItinerary(itineraryId), { id: itineraryId }));
      });
    });
  }

  public static setLocationDefaultLto(locationId: number, ltoId: number) {
    return StoreActionFactory.api(Actions.Locations.SetDefaultLto, api.setLocationDefaultLto(locationId, ltoId));
  }

  public static getItinerariesForEnquiry(enquiryId: number) {
    return StoreActionFactory.api(Actions.Itineraries.GetItinerariesForEnquiry, api.getItinerariesForEnquiry(enquiryId));
  }

  public static deleteEnquiryItinerary(itineraryId: number) {
    return StoreActionFactory.api(Actions.Itineraries.DeleteEnquiryItinerary, api.deleteEnquiryItinerary(itineraryId));
  }

  public static addNewTravelerDetails(newTravelerDetails: INewTravelerDetailsInput) {
    return StoreActionFactory.api(Actions.Enquiries.AddNewTravelerDetails, api.addNewTravelerDetails(newTravelerDetails));
  }

  public static updateTravelerDetails(travelerDetails: ITravelerDetails) {
    const newInput = EntityConverter.travelerDetailsToTravelerDetailsUpdateInput(travelerDetails);
    return StoreActionFactory.api(Actions.Enquiries.UpdateTravelerDetails, api.updateTravelerDetails(newInput));
  }

  public static updateLeadTravelerDetails(enquiryId: number, travelerDetails: ILeadTravelerDetails) {
    const newInput = EntityConverter.leadTravelerDetailsToLeadTravelerDetailsUpdateInput(enquiryId, travelerDetails);
    return StoreActionFactory.api(Actions.Enquiries.UpdateLeadTravelerDetails, api.updateLeadTravelerDetails(newInput));
  }

  public static deleteTravelerDetails(travelerId: number) {
    return StoreActionFactory.api(Actions.Enquiries.DeleteTravelerDetails, api.deleteTravelerDetails(travelerId));
  }

  public static createNonDigitalPayment(nonDigitalPayment: INewNonDigitalPaymentInput) {
    return StoreActionFactory.api(Actions.General.CreateNonDigitalPayment, api.createNonDigitalPayment(nonDigitalPayment));
  }

  public static createRefund(refund: INewRefundInput) {
    return StoreActionFactory.api(Actions.General.CreateRefund, api.createRefund(refund));
  }

  public static allCardTypes = () => StoreActionFactory.api(Actions.General.GetAllCardTypes, api.getCardTypes());

  public static getPaymentsPage = (params: ApiParams.PaymentsFilterSet, page: number = 1, pageSize: number = 20) => {
    const pageNumber = page < 0 ? 1 : page;
    return StoreActionFactory.api(Actions.General.GetPaymentsPage, api.getPayments(params, pageNumber, pageSize), { page, pageSize, params });
  }

  public static updateDigitalPayment = (digitalPaymentUpdate: IDigitalPaymentUpdate) => {
    return StoreActionFactory.api(Actions.General.UpdateDigitalPayment, api.updateDigitalPayment(digitalPaymentUpdate));
  }

  /**
   * Send an itinerary message to the customer, and set the current draft as the current live.
   * A different API to plain enquiry messages, but handling of response uses the same reducer
   */
  public static sendItineraryMessageToCustomer(enquiryId: number, message: string) {
    const input: INewEnquiryMessageInput = { enquiryId, text: message };
    return StoreActionFactory.thunk(async d => {
      await d(StoreActionFactory.api(SharedActions.Messaging.Send, api.sendEnquiryItinerary(input), { enquiryId }));
      await d(StoreActionFactory.api(Actions.Enquiries.GetEnquirySingle, api.getEnquiry(input.enquiryId), { id: input.enquiryId }));
    });
  }

  public static getItinerariesPage(page: number, pageSize: number, params?: ApiParams.ItineraryApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    return StoreActionFactory.api(Actions.Itineraries.GetItinerariesPage, api.getItinerariesPage(pageNumber, pageSize, params), context);
  }

  public static getSnippetsPage(page: number, pageSize: number, params?: ApiParams.SnippetsApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    return StoreActionFactory.api(Actions.Snippets.GetSnippetsPage, api.getSnippetsPage(pageNumber, pageSize, params), context);
  }

  public static updateSnippet(updatedSnippet: ISnippetInput) {
    return StoreActionFactory.api(Actions.Snippets.UpdateSnippet, api.updateSnippet(updatedSnippet));
  }

  public static createSnippet(createdSnippet: ISnippetInput) {
    return StoreActionFactory.api(Actions.Snippets.CreateSnippet, api.createSnippet(createdSnippet));
  }

  public static getSnippetById(snippetId: number) {
    return StoreActionFactory.api(Actions.Snippets.GetSnippetById, api.getSnippetById(snippetId));
  }

  public static users(page: number, pageSize: number, params?: ApiParams.UsersApiParams) {
    const pageNumber = page < 1 ? 1 : page;
    const context = { page, pageSize, params };
    return StoreActionFactory.api(Actions.Users.GetAll, api.getUsersPage(pageNumber, pageSize, params), context);
  }

  public static updateAboutTeamProfiles(key: string, data: IAboutTeamProfiles<number>) {

    const settingInput: ISettingInput = {
      key,
      data,
    };

    return StoreActionFactory.thunk(async d => {
      await d(StoreActionFactory.api(Actions.Settings.UpdateSettings, api.inputSetting(settingInput)));
      await d(this.getAboutTeamProfiles());
    });
  }

  public static getAboutTeamProfiles() {
    return StoreActionFactory.api(Actions.Settings.GetSettings, api.getTeamProfiles(), { key: "teamProfiles" });
  }

  public static getBookingStatistics(range: string) {
    return StoreActionFactory.api(Actions.Ltos.GetBookingStatistics, api.getBookingStatistics(range));
  }
}
